/**
 * Originals catalog story card (Epic 20 S20.5).
 *
 * Cream "book page" card built to the Claude Design prototype (`cards.jsx`
 * StoryCard) with Tailwind + DESIGN.md tokens. The whole card links to the
 * public story page. NO thumbs-up (AD-3 — cut for beta).
 *
 * Finnish-only hardcoded copy (Epic 20 i18n exception).
 *
 * @module components/originals/OriginalsStoryCard
 */

import { Link } from 'react-router-dom';
import type { OriginalsStoryListItem } from '../../hooks/useOriginalsStories';
import { AgeBadge } from './AgeBadge';

const dateFormatter = new Intl.DateTimeFormat('fi-FI');

const DEFAULT_GRADIENT = 'linear-gradient(160deg, #FDF6E9 0%, #e8c4a8 100%)';

interface OriginalsStoryCardProps {
  story: OriginalsStoryListItem;
  /**
   * Tailwind height class(es) for the cover area. Defaults to the catalog size
   * (`h-40`); the front-page "hero" strip passes a taller value to make the
   * cover images larger (Epic 20 front-page tweak). Must be literal class names
   * so Tailwind's JIT picks them up.
   */
  coverHeightClass?: string;
}

export function OriginalsStoryCard({
  story,
  coverHeightClass = 'h-40',
}: OriginalsStoryCardProps): JSX.Element {
  const published = story.publishedAt ? dateFormatter.format(story.publishedAt) : null;

  return (
    <Link
      to={`/originals/${story.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-surface-cream shadow-md transition-dreamy hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2 focus:ring-offset-twilight-deep"
    >
      {/* Cover — image when provided, otherwise gradient + emoji */}
      <div
        className={`relative flex ${coverHeightClass} items-center justify-center`}
        style={
          story.coverImageUrl
            ? undefined
            : { backgroundImage: story.coverGradient ?? DEFAULT_GRADIENT }
        }
      >
        {story.coverImageUrl ? (
          <img
            src={story.coverImageUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="text-5xl" aria-hidden="true">
            {story.coverEmoji ?? '🌙'}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="font-whimsy text-lg text-text-twilight-warm-brown">{story.title}</h3>
        {story.excerpt && (
          <p className="line-clamp-3 text-sm text-text-twilight-warm-brown/80">{story.excerpt}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <AgeBadge ageBand={story.ageBand} />
            <span className="text-xs text-text-twilight-warm-brown/70">
              {published ? `${published} · ` : ''}
              {story.readMinutes} min
            </span>
          </div>
          <span className="inline-flex min-h-11 items-center text-sm font-semibold text-accent-gold">
            Lue satu
            <span
              aria-hidden="true"
              className="ml-1 transition-transform group-hover:translate-x-0.5"
            >
              →
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}
